// src/firebase.js
import { initializeApp } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence, inMemoryPersistence } from "firebase/auth";
import { initializeFirestore, memoryLocalCache } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

// Decide which Firebase project to talk to based on environment.
// Primary source is the current hostname (staging vs prod), with env var fallback.
const APP_ENV = (() => {
  if (typeof window !== "undefined") {
    const host = window.location.hostname || "";
    const isStagingHost = host.includes("staging");
    if (isStagingHost) {
      return "staging";
    }
  }
  if (typeof process !== "undefined" && process.env && process.env.REACT_APP_ENV) {
    return process.env.REACT_APP_ENV;
  }
  return "prod";
})();

// Production Firebase config (current live project)
const prodFirebaseConfig = {
  apiKey: "AIzaSyDebovQLpv9hU85ob-rGmARU2_yL6VOc7M",
  authDomain: "artmosphere-89b64.firebaseapp.com",
  projectId: "artmosphere-89b64",
  storageBucket: "artmosphere-89b64.firebasestorage.app",
  messagingSenderId: "599020841067",
  appId: "1:599020841067:web:62919b83a2b248136ae92e",
  measurementId: "G-KP16Y60Y3Z",
};

// Staging Firebase config is read from environment variables so keys are not hard-coded.
// Create a .env.staging file (not committed to git) with these values from your
// "artmosphere-89b64-staging" web app config.
const stagingFirebaseConfig = {
  apiKey: process.env.REACT_APP_STAGING_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_STAGING_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_STAGING_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STAGING_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_STAGING_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_STAGING_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_STAGING_FIREBASE_MEASUREMENT_ID,
};

const firebaseConfig = APP_ENV === "staging" ? stagingFirebaseConfig : prodFirebaseConfig;

// Staging must have projectId set via REACT_APP_STAGING_FIREBASE_* in .env.local when running build:staging.
// If missing, the app would connect to the wrong project; fail fast so deploy is fixed.
if (APP_ENV === "staging" && !firebaseConfig.projectId) {
  console.error(
    "[Artmosphere] Staging build is missing REACT_APP_STAGING_FIREBASE_PROJECT_ID. " +
    "Add staging Firebase config to .env.local and run: npm run build:staging"
  );
  throw new Error("Staging Firebase config incomplete: set REACT_APP_STAGING_FIREBASE_* in .env.local and use npm run build:staging");
}

// Staging: verify which API key is in the bundle (for Firestore "access control" debugging).
if (APP_ENV === "staging" && firebaseConfig.apiKey && typeof window !== "undefined") {
  const k = String(firebaseConfig.apiKey);
  console.log("[Artmosphere Staging] Firebase API key (last 4 chars):", k.length >= 4 ? k.slice(-4) : "(hidden)");
}

// Single shared Firebase app and Firestore instance. All app code must use this db (no getFirestore() or second initializeApp elsewhere).
const app = initializeApp(firebaseConfig);

// Optional: App Check (abuse protection). Initialize immediately after app so tokens attach to all SDK requests.
// Env-gated: only when REACT_APP_ENABLE_APPCHECK=true and REACT_APP_APPCHECK_SITE_KEY are set.
try {
  const enableAppCheck = String(process.env.REACT_APP_ENABLE_APPCHECK || "").toLowerCase() === "true";
  const siteKey = process.env.REACT_APP_APPCHECK_SITE_KEY;
  if (enableAppCheck && siteKey) {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
    if (typeof window !== "undefined") {
      window.__ARTMO_APPCHECK_ENABLED = true;
    }
  }
} catch (e) {
  console.warn("[Artmosphere] App Check init skipped/failed:", e?.message);
}

// Expose project ID for verification in browser console (e.g. type: __ARTMO_FIREBASE_PROJECT_ID)
if (typeof window !== "undefined") {
  window.__ARTMO_FIREBASE_PROJECT_ID = firebaseConfig.projectId;
}

// Initialize services (Auth, Firestore, Storage will automatically use App Check token when enabled)
const auth = getAuth(app);
// Ensure auth does not rely on IndexedDB; prefer localStorage, then memory fallback
try {
  setPersistence(auth, browserLocalPersistence).catch(() => setPersistence(auth, inMemoryPersistence));
} catch (e) {
  // Last resort: in-memory only
  try { setPersistence(auth, inMemoryPersistence); } catch (_) {}
}
// Firestore: single shared instance. Safari + staging force long-polling to avoid Listen/channel CORS.
const isSafari = typeof window !== 'undefined' && /Safari/.test(navigator.userAgent || '') && !/Chrome/.test(navigator.userAgent || '');
const forceLongPollingEnv = String(process.env.REACT_APP_FIRESTORE_FORCE_LONG_POLLING || '').toLowerCase() === 'true';
const forceLongPolling = APP_ENV === 'staging' || isSafari || forceLongPollingEnv;
const firestoreSettings = forceLongPolling
  ? {
      localCache: memoryLocalCache(),
      experimentalForceLongPolling: true,
      experimentalAutoDetectLongPolling: false,
    }
  : {
      localCache: memoryLocalCache(),
      experimentalAutoDetectLongPolling: true,
    };
const db = initializeFirestore(app, firestoreSettings);
if (typeof window !== 'undefined') {
  window.__ARTMO_FIRESTORE_TRANSPORT = forceLongPolling ? 'long-polling' : 'auto-detect';
  if (forceLongPolling) {
    window.__ARTMO_FIRESTORE_FORCE_POLLING = true;
    console.log('[Firestore] Firestore configured for forced long-polling');
  }
}
const storage = getStorage(app);

// Export all services
export { app, auth, db, storage, firebaseConfig };
